import {useTheme} from "@emotion/react";
import Markdown from "@/elements/Markdown";
import TitleUnderlinedAnimated from "@/elements/TitleUnderlinedAnimated";
import {Text} from "@/elements/text";
import type {SectionWysiwygNew} from "@/generated/api";
import SectionWrapper from "@/layouts/SectionWrapper";
import {ContentWrapper, MultiColumnContainer} from "./styles";

interface B2TexteWYSIWYGNewProps {
  sectionData: SectionWysiwygNew;
  isFirstSection?: boolean;
  isLastSection?: boolean;
  id?: string;
}

/**
 * B2 Texte WYSIWYG New Component
 * Uses CSS Multi-Column Layout for automatic 50/50 column balancing
 *
 * CSS column-count: 2 with column-fill: balance automatically distributes
 * content evenly between two columns without any JavaScript calculation.
 * The browser handles all the layout math natively.
 *
 * Break Control Strategy:
 * - Allows breaks BETWEEN sentences within paragraphs (natural flow)
 * - Prevents breaks INSIDE sentences (orphans: 3, widows: 3, text-wrap: pretty)
 * - Prevents orphaned punctuation after : ; ,
 * - Keeps headings with following content
 *
 * @param {Object} props
 * @param {Object} props.sectionData - ACF section data from WordPress
 * @param {string} props.sectionData.title - H2 title displayed above columns
 * @param {string} props.sectionData.subtitle - Optional subtitle
 * @param {string} props.sectionData.text - WYSIWYG text content to display
 * @param {string} props.sectionData.bg_color - Background color (white/gray)
 * @param {boolean} props.isFirstSection - Is this the first section
 * @param {boolean} props.isLastSection - Is this the last section
 * @param {string} props.id - Section ID
 */
export default function B2TexteWYSIWYGNew({
  sectionData,
  isFirstSection,
  isLastSection,
  id,
}: B2TexteWYSIWYGNewProps) {
  const {bg_color, subtitle, text, title} = sectionData;
  const {breakpoints} = useTheme();
  const {isMobile, md} = breakpoints;

  // Don't render if no text
  if (!text || text.length === 0) {
    return null;
  }

  return (
    <SectionWrapper
      id={id}
      backgroundColor={bg_color}
      isFirstSection={isFirstSection}
      isLastSection={isLastSection}
      styles={{
        paddingBlock: "0px 0px",
      }}
    >
      {/* Title and subtitle in ContentWrapper */}
      <ContentWrapper>
        {!!title?.length && (
          <TitleUnderlinedAnimated styles={{fontSize: "2rem", lineHeight: "2.5rem"}}>
            {title}
          </TitleUnderlinedAnimated>
        )}

        {!!subtitle?.length && (
          <Text as={"h3"} type="titleExtraSmall" styles={{marginBottom: 12}}>
            {subtitle}
          </Text>
        )}
      </ContentWrapper>

      {/* CSS Multi-Column Layout - Automatic 50/50 balance with break control */}
      <MultiColumnContainer isMobile={isMobile} md={md}>
        <Markdown
          textStyles={{
            marginBottom: 27,
            // Allow breaks between sentences, orphan/widow control prevents bad breaks
            orphans: 3,
            widows: 3,
          }}
          h3Styles={{
            marginBottom: 12,
            breakInside: "avoid", // Never break inside headings
            breakAfter: "avoid", // Keep heading with next paragraph
          }}
          wrapperStyles={{
            marginBottom: 27,
            marginTop: 0,
          }}
        >
          {text}
        </Markdown>
      </MultiColumnContainer>
    </SectionWrapper>
  );
}
